import {
  Caption2,
  Link,
  makeStyles,
  mergeClasses,
  tokens,
  Tooltip,
} from '@fluentui/react-components'
import { Clock12Regular, Info12Regular } from '@fluentui/react-icons'
import { useTranslation } from 'react-i18next'
import { useRenewFile } from '../../hooks'
import { formatDayAndMonth, formatTime } from '../../utils/dateFormat'
import {
  getRetentionDisplay,
  resolveExpiresAt,
  type RetentionDisplay,
} from './fileRetentionRow.utils'

const useStyles = makeStyles({
  retentionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    minWidth: 0,
    width: '100%',
  },
  retentionStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    minWidth: 0,
  },
  retentionStatusDays: {
    color: tokens.colorNeutralForeground3,
  },
  retentionStatusTomorrow: {
    color: tokens.colorStatusWarningForeground1,
  },
  retentionStatusToday: {
    color: tokens.colorStatusDangerForeground1,
  },
  retentionClockIcon: {
    flexShrink: 0,
    fontSize: '12px',
  },
  retentionInfoIcon: {
    flexShrink: 0,
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  separator: {
    color: tokens.colorNeutralForeground2,
    flexShrink: 0,
  },
  keepFileButton: {
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase100,
    flexShrink: 0,
  },
})

type FileRetentionRowProps = {
  fileId: string
  expiresAt?: string
}

const FileRetentionRow = ({ fileId, expiresAt }: FileRetentionRowProps) => {
  const styles = useStyles()
  const { t, i18n } = useTranslation()
  const { mutate: renewFile } = useRenewFile()
  const resolvedExpiresAt = resolveExpiresAt(expiresAt)
  if (!resolvedExpiresAt) {
    return null
  }

  const display: RetentionDisplay | null = getRetentionDisplay(resolvedExpiresAt)
  if (!display) {
    return null
  }

  const locale = i18n.language

  const statusClassName = mergeClasses(
    styles.retentionStatus,
    display.variant === 'days' && styles.retentionStatusDays,
    display.variant === 'tomorrow' && styles.retentionStatusTomorrow,
    display.variant === 'today' && styles.retentionStatusToday
  )

  let statusLabel: string
  let policyTooltip: string

  switch (display.variant) {
    case 'days':
      statusLabel = t('files.retention.deletesInDays', { count: display.daysRemaining })
      policyTooltip = t('files.retention.policyTooltipOnDate', {
        date: formatDayAndMonth(resolvedExpiresAt, locale),
      })
      break
    case 'tomorrow':
      statusLabel = t('files.retention.deletesTomorrow')
      policyTooltip = t('files.retention.policyTooltipTomorrow', {
        time: formatTime(resolvedExpiresAt, locale),
      })
      break
    case 'today':
      statusLabel = t('files.retention.deletesToday')
      policyTooltip = t('files.retention.policyTooltipToday', {
        time: formatTime(resolvedExpiresAt, locale),
      })
      break
  }

  return (
    <div className={styles.retentionRow}>
      <span className={statusClassName}>
        <Clock12Regular className={styles.retentionClockIcon} aria-hidden />
        <Caption2>{statusLabel}</Caption2>
      </span>
      <Tooltip content={policyTooltip} relationship="description" appearance="inverted">
        <Info12Regular className={styles.retentionInfoIcon} aria-label={policyTooltip} />
      </Tooltip>
      <Caption2 className={styles.separator} aria-hidden>
        ·
      </Caption2>
      <Link
        as="button"
        inline
        className={styles.keepFileButton}
        onClick={(event) => {
          event.stopPropagation()
          renewFile({ fileId })
        }}>
        {t('files.retention.keepFile')}
      </Link>
    </div>
  )
}

export { FileRetentionRow }
